import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { cacheableGet } from '@satellite/services/httpClient';
import { escapeHtml } from '@satellite/helpers/escapeHtml';
import { AppointmentStatus } from '@nova/core';

/**
 * options: { fields: string[], joins: string[], includeMetaData: boolean }
 */

export default {
  async getAppointmentById(appointmentId, params, options = {}, requestConfig = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(
      `appointment/${appointmentId}?${queryString}`,
      {
        params
      },
      requestConfig
    );
    return getResponse(response, options);
  },
  /**
   * Get appointment by ID with improved API contract.
   *
   * @param {string} appointmentId - Appointment ID
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching
   * @returns {Promise<unknown>} The response data
   */
  async getAppointmentByIdV2(
    appointmentId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = false } = {}
  ) {
    const url = `appointment/${appointmentId}`;

    const axiosResponse = await cacheableGet(url, {
      ...requestConfig,
      enableCache
    });

    return getResponse(axiosResponse, getResponseConfig);
  },
  async getPublicAppointment(appointmentId, options = {}, requestConfig = {}) {
    const { accessToken, ...queryOptions } = options;
    const queryString = getQueryStringFromOptions(queryOptions);
    const response = await axios.get(`appointment/public/${appointmentId}?${queryString}`, {
      ...requestConfig,
      params: {
        ...requestConfig.params,
        ...(accessToken ? { accessToken } : {})
      }
    });
    return getResponse(response, options);
  },
  async createAppointment(params, isClone = false, options = {}, requestConfig = {}) {
    const response = await axios.post(
      `/appointment${isClone ? '?bypassCustomFieldsValidation=true' : ''}`,
      params,
      requestConfig
    );

    this.triggerSocketEvent(response, 'create-Appointment');

    return getResponse(response, options);
  },
  /**
   * @param {string} appointmentId
   * @param {Object} data
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options]
   * @param {import('axios').AxiosRequestConfig} [requestConfig]
   * @returns {Promise<import('@nova/core').IAppointment>}
   */
  async updateAppointment(appointmentId, data, options = {}, requestConfig = {}) {
    const response = await axios.patch(`appointment/${appointmentId}`, data, requestConfig);

    this.triggerSocketEvent(response);

    return getResponse(response, options);
  },
  /**
   * @param {string} appointmentId
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options]
   * @param {import('axios').AxiosRequestConfig} [requestConfig]
   * @returns {Promise<import('@nova/core').IAppointment>}
   */
  async undoLatestStatus(appointmentId, options = {}, requestConfig = {}) {
    const response = await axios.patch(
      `appointment/${appointmentId}/undo-latest-status`,
      null,
      requestConfig
    );

    return getResponse(response, options);
  },
  async cancelAppointment(appointment, reason, options = {}, requestConfig = {}) {
    const notes = reason
      ? `${appointment.notes ?? ''}<br/>Cancellation Reason: ${escapeHtml(reason)}`
      : (appointment.notes ?? '');
    const data = {
      notes,
      status: AppointmentStatus.Cancelled
    };
    const response = await axios.patch(`appointment/${appointment.id}`, data, requestConfig);

    this.triggerSocketEvent(response);

    return getResponse(response, options);
  },
  /**
   * Reschedule an appointment: merge `fields` with notes (optionally appending
   * `reasonLabel` + `reason`), then PATCH via `updateAppointment(appointment.id, data, ...)`.
   *
   * @template TResponse - The type of the response data
   * @param {Object} appointment - Appointment being rescheduled
   * @param {string} appointment.id - Appointment ID used for the PATCH URL
   * @param {string|null|undefined} [appointment.notes] - Existing notes HTML; merged when reason is provided
   * @param {Object} fields - Patch fields spread into the update body (e.g. `start`, `dockId`, `loadTypeId`)
   * @param {string} [fields.start] - New appointment start (ISO 8601)
   * @param {string} [fields.dockId] - New dock ID
   * @param {string} [fields.loadTypeId] - New load type ID
   * @param {string} [reason] - Reschedule reason text; when provided, appended to notes
   * @param {string} reasonLabel - Label prefixed in notes (e.g. "Rescheduling Reason")
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options] - Passed through to `getResponse` (e.g. `includeMetaData`)
   * @param {import('axios').AxiosRequestConfig} [requestConfig] - Axios request config (e.g. `suppressNotification`)
   * @returns {Promise<TResponse>} Same contract as `updateAppointment` / `getResponse`
   */
  async rescheduleAppointment(
    appointment,
    fields,
    reason,
    reasonLabel,
    options = {},
    requestConfig = {}
  ) {
    const notes = reason
      ? `${appointment.notes ?? ''}<br/>${reasonLabel}: ${escapeHtml(reason)}`
      : (appointment.notes ?? '');
    const data = { ...fields, notes };
    return this.updateAppointment(appointment.id, data, options, requestConfig);
  },

  async setAppointmentEta(appointmentId, data, options = {}, requestConfig = {}) {
    const response = await axios.patch(
      `appointment/${appointmentId}/set-eta`,
      {
        eta: data.etaISO,
        reason: data.etaReason
      },
      requestConfig
    );

    this.triggerSocketEvent(response);

    return getResponse(response, options);
  },
  async getAppointments(params, options = {}, requestConfig = {}) {
    const queryString = getQueryStringFromOptions(options);
    const response = await axios.get(
      `appointment?${queryString}`,
      {
        params
      },
      requestConfig
    );

    return getResponse(response, options);
  },
  // By now, this is only valid for helios (not luna)
  // It simulates the socket event for update the appointment at
  // the local session
  triggerSocketEvent(response, eventName = 'update-Appointment') {
    if (this.$app?.$eventHub && response?.status < 400 && response?.data?.data) {
      this.$app.$eventHub.$emit(eventName, response.data.data);
    }
  }
};
